import { memo, useEffect, useRef } from 'react';
import {
	createSortedRowModel,
	rowSortingFeature,
	tableFeatures,
	useTable,
} from '@tanstack/react-table';
import { isEqual } from 'es-toolkit';
import s from './table.module.css';

const keysBigWidth = ['address', 'comments'];

const features = tableFeatures({
	rowSortingFeature,
	sortedRowModel: createSortedRowModel(),
});

// Greek-aware, digit-aware string comparison: 'Ά' next to 'α', and '10' after '9'.
const collator = new Intl.Collator('el', { numeric: true, sensitivity: 'base' });

const isEmpty = value => value === null || value === undefined || value === '';

// One comparator for every column, instead of the default `sortFn: 'auto'`.
// `auto` picks a sort fn by sampling the first 10 rows, so a column that is
// empty at the top of the list (e.g. `platform`, only set on bridge orders)
// resolved to the `basic` comparator — and `basic` is `a > b ? 1 : -1`, which
// answers -1 to BOTH compare(null, 'efood') and compare('efood', null). An
// inconsistent comparator makes Array.sort return arbitrary order, which is
// why some columns sorted into visible nonsense while others looked fine.
const sortFn = (rowA, rowB, columnId) => {
	const a = rowA.getValue(columnId);
	const b = rowB.getValue(columnId);

	// Empty values group together at one end instead of scattering.
	if (isEmpty(a) || isEmpty(b)) return isEmpty(a) ? (isEmpty(b) ? 0 : 1) : -1;

	if (typeof a === 'number' && typeof b === 'number') return a === b ? 0 : a > b ? 1 : -1;

	return collator.compare(String(a), String(b));
};

// `sortUndefined: false` turns off the built-in undefined handling — it only
// tests `=== undefined`, so it would separate undefined cells from null ones.
const defaultColumn = { sortFn, sortUndefined: false };

const initialState = {
	sorting: [
		{
			id: 'index',
			desc: false,
		},
	],
};

const Table = memo(
	({ columns, data, onClick, onEndReached, isLoadingMore }) => {
		const containerRef = useRef(null);
		const sentinelRef = useRef(null);

		const table = useTable({
			features,
			columns,
			data,
			defaultColumn,
			initialState,
		});

		// Infinite scroll. The table scrolls inside .container, not the window, so
		// the observer's root must be the container. Recreated on every append
		// (data.length dep) because an observer only fires on threshold crossings —
		// a sentinel that stays visible after an append would otherwise go silent.
		// A plain scroll listener backs it up: the caller's busy guard makes the
		// two triggers firing together harmless.
		useEffect(() => {
			if (!onEndReached || !sentinelRef.current) {
				return;
			}
			const container = containerRef.current;
			const observer = new IntersectionObserver(
				([entry]) => entry.isIntersecting && onEndReached(),
				{ root: container, rootMargin: '300px' },
			);
			observer.observe(sentinelRef.current);

			const onScroll = () => {
				if (container.scrollHeight - container.scrollTop - container.clientHeight < 300) {
					onEndReached();
				}
			};
			container.addEventListener('scroll', onScroll, { passive: true });

			return () => {
				observer.disconnect();
				container.removeEventListener('scroll', onScroll);
			};
		}, [onEndReached, data?.length]);

		return (
			<div className={s.container} ref={containerRef}>
				<table className={s.table}>
					<thead>
						{table.getHeaderGroups().map(headerGroup => (
							<tr key={headerGroup.id} className={s.tr}>
								{headerGroup.headers.map(header => {
									// The toggle handler is a no-op on a non-sortable column, so the
									// cursor has to follow getCanSort() rather than the handler itself.
									const canSort = header.column.getCanSort();

									return (
										<th
											key={header.id}
											className={`${s.th} ${canSort ? s.thSortable : ''}`}
											onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
										>
											<table.FlexRender header={header} />
										</th>
									);
								})}
							</tr>
						))}
					</thead>
					<tbody
						onClick={
							onClick
								? e => {
										const tr = e.target.closest('tr[data-id]');
										if (tr) onClick(Number(tr.dataset.id) || tr.dataset.id);
									}
								: undefined
						}
					>
						{table.getRowModel().rows.map(row => (
							<tr key={row.id} className={s.tableRow} data-id={row.original.id}>
								{row.getAllCells().map(cell => (
									<td
										key={cell.id}
										className={`${s.td} ${
											keysBigWidth.some(term => cell.column.id.includes(term)) ? s.tdBig : ''
										}`}
									>
										<table.FlexRender cell={cell} />
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
				{onEndReached ? <div ref={sentinelRef} /> : null}
				{isLoadingMore ? (
					<div className='py-2 text-center text-sm text-slate-400'>Φόρτωση…</div>
				) : null}
			</div>
		);
	},

	(prev, next) => {
		const same =
			// Length first: appending pages would otherwise pay a full deep compare
			// of every loaded row just to learn the arrays differ.
			prev.data?.length === next.data?.length &&
			isEqual(prev.data, next.data) &&
			isEqual(prev.columns, next.columns) &&
			prev.onClick === next.onClick &&
			prev.onEndReached === next.onEndReached &&
			prev.isLoadingMore === next.isLoadingMore;

		return same;
	},
);

export default Table;
